import { useState } from 'react';
import { IconSizes, InfoCircleIcon } from '@newcore/icons';
import { Button } from '@newcore/ui/buttons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Text } from '@newcore/ui/typography';
import { LogoVertical } from '@/components/logos';
import { LoginButton } from '@/components/ui/login-button';
import { SskFailureAction, type SskFailureCopy } from '@/utils/ssk-failure';

export interface SskFailureScreenProps {
  copy: SskFailureCopy;
  username?: string;
  supportMailto: string;
  referenceCode: string;
  onRetry: () => void;
  onStartOver: () => void;
}

const EMAIL_SUPPORT_LABEL = 'Email IT support';
const RETRY_LABEL = 'Try again';

const formatFailedAt = () =>
  new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export function SskFailureScreen({
  copy,
  username,
  supportMailto,
  referenceCode,
  onRetry,
  onStartOver,
}: SskFailureScreenProps) {
  const [failedAt] = useState(formatFailedAt);
  // A contact-first bucket without a configured support email falls back to retry.
  const emailIsPrimary =
    copy.primaryAction === SskFailureAction.CONTACT_SUPPORT && !!supportMailto;

  return (
    <div className='min-h-screen flex flex-col items-center px-6 py-10'>
      <LogoVertical className='h-6' />
      <div className='flex-1 flex flex-col items-center justify-center gap-8 w-full max-w-sm text-center'>
        <div className='flex flex-col gap-4'>
          <Text
            as='h1'
            data-testid='ssk-failure-headline'
            size={TextSizes.XL}
            weight={FontWeights.BOLD}
            className='text-balance'
          >
            {copy.headline}
          </Text>
          <Text as='p' size={TextSizes.SM} className='text-secondary'>
            {copy.body}
          </Text>
        </div>

        {username && (
          <div className='flex w-full items-center gap-2 rounded-md border border-brand bg-brand-primary px-4 py-2 text-left'>
            <InfoCircleIcon
              size={IconSizes.SM}
              className='text-brand-secondary shrink-0'
            />
            <Text as='span' size={TextSizes.SM} className='text-secondary'>
              Signed in as{' '}
              <Text
                as='span'
                size={TextSizes.SM}
                weight={FontWeights.MEDIUM}
                className='text-primary'
              >
                {username}
              </Text>
            </Text>
          </div>
        )}

        <div className='flex w-full flex-col items-center gap-5'>
          {emailIsPrimary ? (
            <Button
              asChild
              size={Sizes.LG}
              variant={ButtonVariants.PRIMARY}
              className='w-full'
            >
              <a href={supportMailto}>{EMAIL_SUPPORT_LABEL}</a>
            </Button>
          ) : (
            <Button
              type='button'
              size={Sizes.LG}
              variant={ButtonVariants.PRIMARY}
              className='w-full'
              onClick={onRetry}
            >
              {RETRY_LABEL}
            </Button>
          )}
          {copy.secondary && (
            <Text as='p' size={TextSizes.XS} className='text-secondary'>
              {copy.secondary}
            </Text>
          )}
          <div className='flex w-full items-center gap-3'>
            <hr className='flex-1 border-secondary' />
            <Text as='span' size={TextSizes.XS} className='text-tertiary'>
              or
            </Text>
            <hr className='flex-1 border-secondary' />
          </div>
          <div className='flex items-center gap-6'>
            {!emailIsPrimary && supportMailto && (
              <LoginButton
                asChild
                variant={ButtonVariants.LINK_SECONDARY}
                className='text-secondary underline'
              >
                <a href={supportMailto}>{EMAIL_SUPPORT_LABEL}</a>
              </LoginButton>
            )}
            <LoginButton
              type='button'
              variant={ButtonVariants.LINK_SECONDARY}
              className='text-secondary underline'
              onClick={onStartOver}
            >
              Start over
            </LoginButton>
          </div>
        </div>

        <Text as='p' size={TextSizes.XS} className='text-tertiary mt-4'>
          Reference {referenceCode} · {failedAt}
        </Text>
      </div>
    </div>
  );
}
